import { useEffect } from "react";
import { Box, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import { useTheme } from "../../hooks";
import { toSurfaceTint } from "../../utils/colorManager";
import { useTranslation } from "react-i18next";
import { useSchoolFee } from "../../context/schoolFeeContext";
import { formatCurrency } from "../../utils";
import { SchoolFeeFeeLine } from "../../models/schoolFee";

// Every fee the selected student's enrolments carry, flattened in the order
// the server sent them.
//
// ⛔ LOCKED (TODO-187, resolved 2026-08-25 by PR #738): the server returns only
// the fees of the service the payer arrived through, so there is NO client-side
// grouping or filtering by `variant` here. Duplicating a server rule in a
// second place is how the two drift, and the payer would then see a set the
// payment leg disagrees with.
const applicableFees = (
  enrolments: { fees: SchoolFeeFeeLine[] }[]
): SchoolFeeFeeLine[] => enrolments.flatMap(enrolment => enrolment.fees);

const toSelected = (fee: SchoolFeeFeeLine) => ({
  fee_assignment_id: fee.fee_assignment_id,
  name: fee.name,
  variant: fee.variant,
  amount: fee.amount,
  currency: fee.currency,
  supports_partial_payments: fee.supports_partial_payments,
});

/**
 * PAY-05. Exactly one fee is payable per payment in V1, so this is a radio
 * group and not a checklist -- the single-selection rule is expressed by the
 * control rather than validated after the fact.
 */
const FeeSelect = () => {
  const { t } = useTranslation();
  const { student, selectedFee, selectFee } = useSchoolFee();

  const { theme } = useTheme();
  const fees = applicableFees(student?.enrolments ?? []);
  const only = fees.length === 1 ? fees[0] : null;

  // A single fee is the only answer, so the payer is not asked to pick it.
  useEffect(() => {
    if (only && !selectedFee) selectFee(toSelected(only));
  }, [only, selectedFee, selectFee]);

  if (fees.length === 0) {
    return (
      <Box mb={4}>
        <Typography variant="body1" fontWeight="700">
          {t("schoolfee.fee.none_title")}
        </Typography>
        <Typography sx={{ fontSize: 13 }}>
          {t("schoolfee.fee.none_body")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box mb={4}>
      <Typography sx={{ fontSize: 14, fontWeight: 600 }} mb={1.5}>
        {`${t("schoolfee.fee.select_label")}*`}
      </Typography>

      <RadioGroup
        value={selectedFee?.fee_assignment_id ?? ""}
        onChange={event => {
          const chosen = fees.find(
            fee => fee.fee_assignment_id === event.target.value
          );

          // selectFee already drops a stale amount when the fee changes; that
          // invalidation is not repeated here, because two places deciding
          // when an amount is stale is how one of them stops.
          selectFee(chosen ? toSelected(chosen) : null);
        }}
        sx={{ gap: 1.5 }}
      >
        {fees.map(fee => {
          const chosen = selectedFee?.fee_assignment_id === fee.fee_assignment_id;

          return (
            <FormControlLabel
              key={fee.fee_assignment_id}
              value={fee.fee_assignment_id}
              control={<Radio />}
              sx={{
                m: 0,
                p: 1.5,
                borderRadius: 3,
                border: "1px solid",
                borderColor: chosen ? theme.color_primary : "divider",
                // The dummy's chosen row is washed with the brand's own tint,
                // not a neutral: the surface and the border are two variants
                // of the same colour, so the selection reads as one control.
                bgcolor: chosen
                  ? toSurfaceTint(theme.color_secondary)
                  : "background.paper",
                "& .MuiFormControlLabel-label": { flex: 1, minWidth: 0 },
              }}
              label={
                // Name and its context on the left, the amount against the
                // row's right edge, as the dummy draws `.pf-opt`.
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={600}>{fee.name}</Typography>
                    <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                      {t("schoolfee.fee.amount_owed")}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatCurrency(fee.amount, undefined, fee.currency)}
                  </Typography>
                </Box>
              }
            />
          );
        })}
      </RadioGroup>

      {!selectedFee && (
        <Typography sx={{ fontSize: 13, mt: 1.5 }}>
          {t("schoolfee.fee.choose")}
        </Typography>
      )}
    </Box>
  );
};

export default FeeSelect;
